/* import __COLOCATED_TEMPLATE__ from './data-export.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { DrillInSummary } from './custom/report/underlying-data-modal';
import { action } from '@ember/object';
import type { FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import Range from 'embercom/models/reporting/range';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { inject as service } from '@ember/service';
import { compact, flatten, isEmpty } from 'underscore';
import { isPresent } from '@ember/utils';
import type { FilterItem } from 'embercom/objects/reporting/unified/metrics/types';
import type { Attribute } from 'embercom/objects/reporting/unified/datasets/types';
import { changeFilter } from 'embercom/lib/reporting/custom/filter-helpers';
import type {
  LogicalFilter,
  LogicalFilterOperator,
} from 'embercom/components/reporting/custom/filters';
import { buildFiltersForDatasetConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { groupDropDownItems } from './custom/chart-builder/chart-builder-helpers';
import type IntlService from 'embercom/services/intl';
import type { DropDownItem } from './custom/chart-builder/chart-builder-helpers';
import type ReportingCsvExport from 'embercom/services/reporting-csv-export';
import type Store from '@ember-data/store';
import {
  SchedulePreset,
  type DatasetExportConfig,
} from 'embercom/models/reporting/dataset-export-schedule';
import { cached } from 'tracked-toolbox';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';
import moment from 'moment-timezone';
import { captureException } from 'embercom/lib/sentry';

interface GroupedDropDownItem {
  heading: string;
  items: DropDownItem[];
}

interface Args {}

export default class DataExport extends Component<Args> {
  @service router: any;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare appService: AppService;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare intl: IntlService;
  @service declare reportingCsvExport: ReportingCsvExport;
  @service declare store: Store;
  @service declare intercomEventService: any;
  @service declare modalService: any;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;

  @tracked filters: LogicalFilter = {
    type: 'and',
    filters: [],
  };
  @tracked columns: any[] = this.getDefaultColumns();
  @tracked summary: DrillInSummary = {
    retrievedCount: 0,
    totalCount: 0,
    isRestricted: true,
  };

  @tracked sortBy = this.getDefaultSortBy();
  @tracked sortDirection: 'asc' | 'desc' = 'desc';
  @tracked timezone = this.appTimezone;
  @tracked pageFrom = 0;
  @tracked datasetId = 'conversation';
  @tracked isNewExportSideDrawerOpen = false;
  @tracked isManageExportSideDrawerOpen = false;

  previousSelection = this.columns;
  @tracked selectedDropdownItems = this.columns;
  @tracked pendingColumnUpdate: string[] | null = null;

  get appTimezone() {
    return this.appService.app.timezone;
  }

  @cached
  get exportDateRange(): Range {
    return Range.createFromPreset('past_4_weeks', this.timezone);
  }

  get conversationAttributeDescriptors() {
    return this.store.peekAll('conversation-attributes/descriptor');
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.loadPersistedDataset();
    this.loadPersistedDateRangeForCurrentDataset();
    this.loadPersistedFiltersForCurrentDataset();
  }

  // ----- Persistence helpers -----
  private get storagePrefix(): string {
    return `reporting:${this.appService.app.id}:data-export`;
  }

  private get datasetStorageKey(): string {
    return `${this.storagePrefix}:datasetId`;
  }

  private filtersStorageKey(): string {
    return `${this.storagePrefix}:filters`;
  }

  private dateRangeStorageKey(forDatasetId: string = this.datasetId): string {
    return `${this.storagePrefix}:dateRange:${forDatasetId}`;
  }

  private safelyReadJSON<T>(key: string): T | null {
    try {
      let raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch (e) {
      captureException(e);
      return null;
    }
  }

  private safelyWriteJSON<T>(key: string, value: T) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      captureException(e);
    }
  }

  private safelyRemoveItem(key: string) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      captureException(e);
    }
  }

  private persistDatasetId() {
    try {
      localStorage.setItem(this.datasetStorageKey, this.datasetId);
    } catch (e) {
      captureException(e);
    }
  }

  private persistFiltersToLocalStorage() {
    let key = this.filtersStorageKey();
    if (this.filters && Array.isArray(this.filters.filters) && this.filters.filters.length > 0) {
      this.safelyWriteJSON(key, this.filters);
    } else {
      this.safelyRemoveItem(key);
    }
  }

  private persistDateRangeToLocalStorage() {
    let key = this.dateRangeStorageKey();
    let payload = {
      selection: this.exportDateRange.selectedRange,
      start: this.exportDateRange.start,
      end: this.exportDateRange.end,
    } as { selection: string | undefined; start: string | null; end: string | null };
    if (payload.selection) {
      this.safelyWriteJSON(key, payload);
    } else {
      this.safelyRemoveItem(key);
    }
  }

  private loadPersistedDataset() {
    let stored = localStorage.getItem(this.datasetStorageKey);
    if (stored && typeof stored === 'string' && stored !== this.datasetId) {
      this.datasetId = stored;
      this.sortBy = this.getDefaultSortBy();
      this.columns = this.getDefaultColumns();
      this.resetPageFrom();
      this.resetFilters();
    }
  }

  private loadPersistedFiltersForCurrentDataset() {
    let persisted = this.safelyReadJSON<LogicalFilter>(this.filtersStorageKey());
    if (persisted) {
      this.filters = persisted;
      this.resetFilters();
      this.resetPageFrom();
    }
  }

  private loadPersistedDateRangeForCurrentDataset() {
    let persisted = this.safelyReadJSON<{ selection?: string; start?: string; end?: string }>(
      this.dateRangeStorageKey(),
    );
    if (persisted && persisted.selection) {
      if (persisted.selection === 'custom' && persisted.start && persisted.end) {
        this.exportDateRange.updateRange(
          moment.tz(persisted.start, this.timezone),
          moment.tz(persisted.end, this.timezone),
        );
      } else {
        this.exportDateRange.setDefaultRange(persisted.selection);
      }
    }
  }

  getDefaultColumns() {
    let cachedSelection = this.reportingUnderlyingDataService.loadSelectedDatasetColumnFromCache(
      this.datasetId,
    );
    if (cachedSelection.length > 0) {
      return cachedSelection;
    }

    return this.getTablePropertiesFromDatasetAttributes([])
      .filter((property) => {
        return (
          !property.value.includes('custom_data') &&
          !(
            property.value.includes('conversation_custom_fields') &&
            property.group !== 'system.attribute'
          ) &&
          !property.value.includes('tickets.data')
        );
      })
      .map((property) => property.value);
  }

  getDefaultSortBy() {
    return this.reportingUnderlyingDataService.getDatasetTimeAttribute(this.datasetId);
  }

  @action
  setDatasetId(datasetId: string) {
    this.datasetId = datasetId;
    this.sortBy = this.getDefaultSortBy();
    this.columns = this.getDefaultColumns();
    this.resetPageFrom();
    this.resetFilters();
    this.persistDatasetId();
    this.loadPersistedDateRangeForCurrentDataset();
    this.loadPersistedFiltersForCurrentDataset();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'dataset_changed',
      object: 'reporting_dataset_export',
      dataset_id: this.datasetId,
    });
  }

  resetFilters() {
    let compatibleFilters = this.filters.filters.every((filter) => {
      let properties = this.filterableDatasetProperties;
      return properties.some((property) => property.id === filter.data.attribute);
    });

    if (!compatibleFilters) {
      this.filters = {
        type: 'and',
        filters: [],
      };
    }
  }

  @action
  updateDrillInSummary(summary: DrillInSummary) {
    this.summary = summary;
  }

  @action
  onSortUpdate(sortBy: string, sortDirection: 'asc' | 'desc') {
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
  }

  get rowsPerPage() {
    return 100;
  }

  @action
  resetPageFrom() {
    this.pageFrom = 0;
  }

  @action
  updatePageFrom(pages: number) {
    this.pageFrom += pages;
  }

  private getTablePropertiesFromDatasetAttributes(currentColumns: string[] = this.columns) {
    let attributes = this.reportingMetrics.getVisibleDatasetAttributesFor(this.datasetId);

    return compact([...attributes]).map((attribute: Attribute) => {
      return {
        text: this.reportingMetrics.translateKey(attribute.name) || '',
        value: attribute.id,
        isSelected: currentColumns.indexOf(attribute.id) !== -1,
        icon: attribute.icon,
        headerName: this.reportingMetrics.translateKey(attribute.headerName) || undefined,
        group: attribute.group,
      };
    });
  }

  get tableProperties(): FilterItem[] {
    return this.getTablePropertiesFromDatasetAttributes();
  }

  get filterableDatasetProperties() {
    let properties = this.reportingMetrics.getVisibleDatasetAttributesFor(this.datasetId);

    properties = flatten([
      ...properties,
      this.reportingMetrics.getFilterableDatasetCdaAttributesFor(this.datasetId),
    ]);
    return properties;
  }

  @action
  onFiltersChanged(
    index: number,
    attribute: { id: string; field: string },
    values: string[],
    operator: string | null,
  ) {
    this.filters = changeFilter(this.filters, index, attribute, values, operator);
    this.resetPageFrom();
    this.applyDateRange(values);
    this.persistFiltersToLocalStorage();
    this.persistDateRangeToLocalStorage();
  }

  applyDateRange(dateRange: any) {
    this.exportDateRange.selectedRange = dateRange.selectedRange;

    if (dateRange.selectedRange === 'custom') {
      this.exportDateRange.updateRange(dateRange.startMoment, dateRange.endMoment);
    }
    this.persistDateRangeToLocalStorage();
  }

  @action
  onFiltersCleared() {
    this.filters = {
      type: 'and',
      filters: [],
    };
    this.resetPageFrom();
    this.persistFiltersToLocalStorage();
  }

  @action
  applyLogicalFilterOperator(operator: LogicalFilterOperator) {
    this.filters = {
      type: operator,
      filters: this.filters.filters,
    };
    this.persistFiltersToLocalStorage();
  }

  get hasData() {
    return this.summary.totalCount > 0;
  }

  get isCSVExportEnabled() {
    return this.isDataAndColumnsPresent && this.isWithinRowLimit;
  }

  get isDataAndColumnsPresent() {
    return this.hasData && isPresent(compact(this.columns));
  }

  get shouldDisableOldExport() {
    return !this.appService.app.canUseFeature('legacy-data-export-enabled');
  }

  get groupedTableProperties() {
    let allAttributes = this.reportingMetrics.getVisibleDatasetAttributesFor(this.datasetId);
    let groupedProperties = groupDropDownItems(
      this.tableProperties as DropDownItem[],
      allAttributes,
      this.intl,
    );

    return [...groupedProperties];
  }

  get allColumns(): GroupedDropDownItem[] {
    return this.groupedTableProperties;
  }

  get filteredAllColumns(): GroupedDropDownItem[] {
    return this.filteredGroupedTableProperties;
  }

  get filteredAllProperties() {
    return this.tableProperties.filter((property) => {
      return (
        !property.value.includes('custom_data') &&
        !(
          property.value.includes('conversation_custom_fields') &&
          property.group !== 'system.attribute'
        ) &&
        !property.value.includes('tickets.data')
      );
    });
  }

  get filteredGroupedTableProperties(): GroupedDropDownItem[] {
    let allAttributes = this.reportingMetrics.getVisibleDatasetAttributesFor(this.datasetId);

    let groupedProperties = groupDropDownItems(
      this.filteredAllProperties as DropDownItem[],
      allAttributes,
      this.intl,
    );

    return [...groupedProperties];
  }

  get shouldDisableScheduleButton() {
    return (
      this.summary.isRestricted || isEmpty(this.exportDateRange.getSelectedRangeFromCurrentTime())
    );
  }

  get shouldDisableScheduleButtonTooltip() {
    return !this.shouldDisableScheduleButton; // we show tooltip only in case button is disabled
  }

  @action
  setColumns(columns: string[]) {
    this.columns = this.reportingUnderlyingDataService.getOrderedDatasetAttributeIds(
      columns,
      this.previousSelection,
      this.datasetId,
    );

    this.reportingUnderlyingDataService.setDatasetColumnSelectionInCache(
      this.datasetId,
      this.columns,
    );

    this.previousSelection = this.columns;
  }

  @action
  onSelectionChange(selection: string[]) {
    this.selectedDropdownItems = selection.uniq();
    this.pendingColumnUpdate = this.selectedDropdownItems;
  }

  @action
  onCloseColumnSelector() {
    if (this.pendingColumnUpdate && this.previousSelection !== this.pendingColumnUpdate) {
      this.setColumns(this.pendingColumnUpdate);
      this.resetPageFrom();
      this.pendingColumnUpdate = null;
    }
  }

  get countSummary() {
    return this.intl.t('reporting.data-table.view-summary', {
      currentCount: this.intl.formatNumber(this.summary.retrievedCount),
      totalCount: this.intl.formatNumber(this.summary.totalCount),
    });
  }

  get isWithinRowLimit() {
    return this.summary.totalCount <= 10_000_000;
  }

  get exportTimeRange(): FlexibleQueryTimeRange {
    let dataModel;

    let datasetTimeAttribute =
      this.reportingUnderlyingDataService
        .getDatasetTimeAttribute(this.datasetId)
        ?.split('.')
        .pop() || 'first_user_conversation_part_created_at';

    return {
      start: this.exportDateRange.startMoment.valueOf(),
      end: this.exportDateRange.endMoment.valueOf(),
      interval: this.exportDateRange.interval,
      property: datasetTimeAttribute,
      data_model: dataModel,
    };
  }

  get sendViaMail() {
    return this.summary.totalCount > 10_000;
  }

  @action
  exportCsv() {
    let analyticsEvent = { object: 'reporting_dataset_export', rows: this.summary.totalCount };
    this.reportingCsvExport.exportCsv(
      this.datasetId,
      this.filters,
      this.columns,
      this.exportTimeRange,
      this.sortBy,
      this.sortDirection,
      false,
      analyticsEvent,
      this.timezone,
      this.sendViaMail,
    );
  }

  @action
  onHoverExportButton() {
    if (this.summary.totalCount > 10_000_000) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'hovered_disabled_export_button',
        object: 'reporting_dataset_export',
        row_count: this.summary.totalCount,
      });
    } else if (this.summary.totalCount === 0) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'hovered_disabled_export_button',
        object: 'reporting_dataset_export',
        row_count: this.summary.totalCount,
      });
    }
  }

  get exportButtonTooltip() {
    if (this.summary.totalCount > 10_000_000) {
      return this.intl.t(
        'components.reporting.custom.report.data-table.export-csv-limit-exceeded-tooltip',
      );
    } else {
      return this.intl.t('reporting.data-export.export-csv-tooltip');
    }
  }

  get exportConfig(): DatasetExportConfig {
    return {
      datasetId: this.datasetId,
      sortByAttributeId: this.sortBy,
      attributeIds: this.columns,
      filter: buildFiltersForDatasetConfig(this.datasetId, this.filters),
      dateRangeSelection: this.exportDateRange.getSelectedRangeFromCurrentTime() || 'past_4_weeks',
    };
  }

  get schedule() {
    return (
      this.store
        .peekAll('reporting/dataset-export-schedule')
        .rejectBy('isDeleted', true)
        .findBy('isNew', true) || this.defaultSchedule()
    );
  }

  private defaultSchedule() {
    let model = this.store.createRecord('reporting/dataset-export-schedule');
    let exportRule = this.datasetExportScheduleService.exportRuleFrom(
      SchedulePreset.DAILY,
      this.timezone,
    );
    model.set('name', '');
    model.set('exportRule', exportRule);
    model.set('exportConfig', this.exportConfig);
    return model;
  }

  @action
  openNewExportSideDrawer() {
    this.isNewExportSideDrawerOpen = true;
  }

  @action
  openManageExportSideDrawer() {
    this.isManageExportSideDrawerOpen = true;
  }

  @action
  closeNewExportSideDrawer() {
    this.isNewExportSideDrawerOpen = false;
  }

  @action
  closeManageExportSideDrawer() {
    this.isManageExportSideDrawerOpen = false;
  }

  @action
  transitionToOldDataExport() {
    this.router.transitionTo('apps.app.reports.customer-support.export');
    this.intercomEventService.trackAnalyticsEvent({
      action: 'transition_to_old_data_export',
      object: 'customer_support_export',
    });
  }

  get scheduleOptions() {
    return [
      {
        items: [
          {
            text: this.intl.t('components.reporting.custom.report.data-table.new-schedule-export'),
            onSelectItem: this.openNewExportSideDrawer,
          },
          {
            text: this.intl.t(
              'components.reporting.custom.report.data-table.manage-schedule-export',
            ),
            onSelectItem: this.openManageExportSideDrawer,
          },
        ],
      },
    ];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::DataExport': typeof DataExport;
    'reporting/data-export': typeof DataExport;
  }
}
